// ============================================================
// src/shack/shackState.js
// Estado central del Shack 3D
// ============================================================

function crearEstadoInicial() {
  return {
    renderer: null,
    scene: null,
    camera: null,
    sceneBg: null,
    cameraBg: null,
    animId: null,
    resizeObserver: null,
    needsRender: true,

    interactionAbortController: null,
    hotkeysAbortController: null,
    runtimeListenersActivos: false,

    host: null,
    scrollWrap: null,
    stageRoot: null,
    overlay: null,
    svg: null,
    canvas: null,
    toolbar: null,

    raycaster: null,
    mouse: null,

    listo: false,
    readOnly: false,
    usuarioUid: null,
    saveTimer: null,
    tooltipConfigurado: false,

    thumbs: new Map(),
    imagenesCache: new Map(),
    frameModelTextureCache: new Map(),

    items: [],
    meshes: [],
    pickables: [],

    helperRect: null,
    gridObjects: { trasera: null, lateral: null },
    pickWallMeshes: { trasera: null, lateral: null },

    calibration: null,
    calibrationMode: false,
    handleEls: {},
    outlineEls: {},

    selection: {
      itemKey: null,
      selectedKeys: new Set(),
    },

    drag: {
      activo: false,
      tipo: null, // 'item' | 'handle' | 'pan'
      itemKey: null,
      wall: null,
      cornerKey: null,
      startX: 0,
      startY: 0,
      startTx: 0,
      startTy: 0,
      startPx: 0,
      startPy: 0,
      startU: 0,
      startV: 0,
      startUVs: null, // Map<key, {u,v}> para arrastre de grupo
    },

    vp: {
      escala: 1,
      tx: 0,
      ty: 0,
      zoom: 1,
      px: 0,
      py: 0,
    },

    imageView: {
      x: 0,
      y: 0,
      w: 0,
      h: 0,
    },

    stageSize: {
      w: 0,
      h: 0,
    },
  };
}

const state = crearEstadoInicial();

// ============================================================
// GETTERS
// ============================================================

export function getShackState() {
  return state;
}

export function getRenderer() {
  return state.renderer;
}

export function getScene() {
  return state.scene;
}

export function getCamera() {
  return state.camera;
}

export function getSceneBg() {
  return state.sceneBg;
}

export function getCameraBg() {
  return state.cameraBg;
}

export function getAnimId() {
  return state.animId;
}

export function getResizeObserver() {
  return state.resizeObserver;
}

export function getInteractionAbortController() {
  return state.interactionAbortController;
}

export function getHotkeysAbortController() {
  return state.hotkeysAbortController;
}

export function getRuntimeListenersActivos() {
  return state.runtimeListenersActivos;
}

export function getHost() {
  return state.host;
}

export function getScrollWrap() {
  return state.scrollWrap;
}

export function getStageRoot() {
  return state.stageRoot;
}

export function getOverlay() {
  return state.overlay;
}

export function getSvg() {
  return state.svg;
}

export function getCanvas() {
  return state.canvas;
}

export function getToolbar() {
  return state.toolbar;
}

export function getRaycaster() {
  return state.raycaster;
}

export function getMouse() {
  return state.mouse;
}

export function getListo() {
  return state.listo;
}

export function getUsuarioUid() {
  return state.usuarioUid;
}

export function getSaveTimer() {
  return state.saveTimer;
}

export function getTooltipConfigurado() {
  return state.tooltipConfigurado;
}

export function getItems() {
  return state.items;
}

export function getMeshes() {
  return state.meshes;
}

export function getPickables() {
  return state.pickables;
}

export function getHelperRect() {
  return state.helperRect;
}

export function getGridObjects() {
  return state.gridObjects;
}

export function getPickWallMeshes() {
  return state.pickWallMeshes;
}

export function getCalibration() {
  return state.calibration;
}

export function getCalibrationMode() {
  return state.calibrationMode;
}

export function getHandleEls() {
  return state.handleEls;
}

export function getOutlineEls() {
  return state.outlineEls;
}

export function getSelection() {
  return state.selection;
}

export function getDrag() {
  return state.drag;
}

export function getViewport() {
  return state.vp;
}

export function getImageView() {
  return state.imageView;
}

export function getStageSize() {
  return state.stageSize;
}

export function getThumbs() {
  return state.thumbs;
}

export function getImagenesCache() {
  return state.imagenesCache;
}

export function getFrameModelTextureCache() {
  return state.frameModelTextureCache;
}

// ============================================================
// SETTERS BÁSICOS
// ============================================================

export function setRenderer(value) {
  state.renderer = value;
}

export function setScene(value) {
  state.scene = value;
}

export function setCamera(value) {
  state.camera = value;
}

export function setSceneBg(value) {
  state.sceneBg = value;
}

export function setCameraBg(value) {
  state.cameraBg = value;
}

export function setAnimId(value) {
  state.animId = value;
}

export function setResizeObserver(value) {
  state.resizeObserver = value;
}

export function setInteractionAbortController(value) {
  state.interactionAbortController = value;
}

export function setHotkeysAbortController(value) {
  state.hotkeysAbortController = value;
}

export function setRuntimeListenersActivos(value) {
  state.runtimeListenersActivos = Boolean(value);
}

export function setHost(value) {
  state.host = value;
}

export function setScrollWrap(value) {
  state.scrollWrap = value;
}

export function setStageRoot(value) {
  state.stageRoot = value;
}

export function setOverlay(value) {
  state.overlay = value;
}

export function setSvg(value) {
  state.svg = value;
}

export function setCanvas(value) {
  state.canvas = value;
}

export function setToolbar(value) {
  state.toolbar = value;
}

export function setRaycaster(value) {
  state.raycaster = value;
}

export function setMouse(value) {
  state.mouse = value;
}

export function setListo(value) {
  state.listo = Boolean(value);
}

export function getReadOnly() {
  return state.readOnly;
}

export function setReadOnly(value) {
  state.readOnly = Boolean(value);
  state.listo = false;
}

export function setUsuarioUid(value) {
  state.usuarioUid = value;
}

export function setSaveTimer(value) {
  state.saveTimer = value;
}

export function setTooltipConfigurado(value) {
  state.tooltipConfigurado = Boolean(value);
}

export function setItems(value) {
  state.items = Array.isArray(value) ? value : [];
}

export function setMeshes(value) {
  state.meshes = Array.isArray(value) ? value : [];
}

export function setPickables(value) {
  state.pickables = Array.isArray(value) ? value : [];
}

export function setHelperRect(value) {
  state.helperRect = value;
}

export function setGridObjects(value) {
  state.gridObjects = value ?? { trasera: null, lateral: null };
}

export function setPickWallMeshes(value) {
  state.pickWallMeshes = value ?? { trasera: null, lateral: null };
}

export function setCalibration(value) {
  state.calibration = value;
}

export function setCalibrationMode(value) {
  state.calibrationMode = Boolean(value);
}

export function setHandleEls(value) {
  state.handleEls = value ?? {};
}

export function setOutlineEls(value) {
  state.outlineEls = value ?? {};
}

export function setThumbs(value) {
  state.thumbs = value instanceof Map ? value : new Map();
}

export function setImagenesCache(value) {
  state.imagenesCache = value instanceof Map ? value : new Map();
}

export function setFrameModelTextureCache(value) {
  state.frameModelTextureCache = value instanceof Map ? value : new Map();
}

export function setSelection(value) {
  state.selection = value ?? { itemKey: null, selectedKeys: new Set() };
}

export function setDrag(value) {
  state.drag = value ?? {
    activo: false,
    tipo: null,
    itemKey: null,
    wall: null,
    cornerKey: null,
    startX: 0,
    startY: 0,
    startTx: 0,
    startTy: 0,
    startPx: 0,
    startPy: 0,
  };
}

export function setViewport(value) {
  state.vp = value ?? { escala: 1, tx: 0, ty: 0, zoom: 1, px: 0, py: 0 };
}

// ============================================================
// HELPERS DE ESTADO
// ============================================================

export function clearSelection() {
  state.selection.itemKey = null;
  state.selection.selectedKeys.clear();
}

export function setSelectedItemKey(itemKey) {
  state.selection.itemKey = itemKey ?? null;
}

export function getSelectedItemKey() {
  return state.selection.itemKey ?? null;
}

export function selectedItem() {
  return state.items.find((i) => i.shackKey === state.selection.itemKey) ?? null;
}

export function itemByKey(key) {
  return state.items.find((i) => i.shackKey === key) ?? null;
}

export function meshByItemKey(key) {
  return state.meshes.find((m) => m?.userData?.itemKey === key) ?? null;
}

export function resetDrag() {
  state.drag.activo = false;
  state.drag.tipo = null;
  state.drag.itemKey = null;
  state.drag.wall = null;
  state.drag.cornerKey = null;
  state.drag.startX = 0;
  state.drag.startY = 0;
  state.drag.startTx = 0;
  state.drag.startTy = 0;
  state.drag.startPx = 0;
  state.drag.startPy = 0;
  state.drag.startU = 0;
  state.drag.startV = 0;
  state.drag.startUVs = null;
}

export function patchDrag(parcial = {}) {
  state.drag = {
    ...state.drag,
    ...parcial,
  };
}

export function patchViewport(parcial = {}) {
  state.vp = {
    ...state.vp,
    ...parcial,
  };
}

export function setImageView(parcial = {}) {
  state.imageView = {
    ...state.imageView,
    ...parcial,
  };
}

export function setStageSize(parcial = {}) {
  state.stageSize = {
    ...state.stageSize,
    ...parcial,
  };
}

export function pushMesh(mesh) {
  state.meshes.push(mesh);
}

export function pushPickable(obj) {
  state.pickables.push(obj);
}

export function pushItem(item) {
  state.items.push(item);
}

export function replaceItemByKey(key, itemActualizado) {
  const idx = state.items.findIndex((i) => i.shackKey === key);
  if (idx >= 0) {
    state.items[idx] = itemActualizado;
  }
}

export function patchItemByKey(key, parcial = {}) {
  const idx = state.items.findIndex((i) => i.shackKey === key);
  if (idx >= 0) {
    state.items[idx] = {
      ...state.items[idx],
      ...parcial,
    };
  }
}

export function removeItemByKey(key) {
  state.items = state.items.filter((i) => i.shackKey !== key);
}

export function clearItems() {
  state.items = [];
}

export function clearMeshes() {
  state.meshes = [];
}

export function clearPickables() {
  state.pickables = [];
}

export function clearThumbs() {
  state.thumbs = new Map();
}

export function clearImagenesCache() {
  state.imagenesCache.clear();
}

export function clearFrameModelTextureCache() {
  state.frameModelTextureCache.clear();
}

export function nextZIndex() {
  if (!state.items.length) return 1;
  return Math.max(...state.items.map((i) => i.zIndex || 0)) + 1;
}

export function ordenarPorZIndex() {
  state.items.sort((a, b) => (a.zIndex || 0) - (b.zIndex || 0));
}

export function sameWallVisibleItems() {
  const sel = selectedItem();
  if (!sel) return [];

  return state.items
    .filter((i) => i.visible !== false && i.wall === sel.wall)
    .sort((a, b) => (a.zIndex || 0) - (b.zIndex || 0));
}

export function guardarDebounced(callback, delay = 260) {
  if (state.saveTimer) {
    clearTimeout(state.saveTimer);
  }

  state.saveTimer = setTimeout(async () => {
    try {
      await callback();
    } catch (err) {
      console.error('[Shack3D] Error en guardarDebounced:', err);
    }
  }, delay);
}

export function cancelarGuardadoDebounced() {
  if (state.saveTimer) {
    clearTimeout(state.saveTimer);
    state.saveTimer = null;
  }
}

export function marcarRender() {
  state.needsRender = true;
}

// ============================================================
// RESET GENERAL
// ============================================================

export function resetShackState() {
  const fresh = crearEstadoInicial();

  Object.keys(fresh).forEach((key) => {
    state[key] = fresh[key];
  });
}